import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { MapPin, ShieldCheck, ImageOff, Heart } from "lucide-react";

export function ListingCard({ listing, favorited, onToggleFavorite, index = 0 }) {
  const navigate = useNavigate();
  const img = resolveImage(listing.images?.[0]);
  const reduced = listing.original_price != null && listing.original_price > listing.price;

  return (
    <div
      data-testid={`listing-card-${listing.id}`}
      onClick={() => navigate(`/listing/${listing.id}`)}
      className="group cursor-pointer overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5 animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-secondary">
        {img ? (
          <img
            src={img}
            alt={listing.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-8 w-8" />
          </div>
        )}
        {onToggleFavorite && (
          <button
            data-testid={`favorite-btn-${listing.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(listing.id); }}
            className="absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur transition hover:scale-110"
            aria-label="Save listing"
          >
            <Heart className={`h-4 w-4 ${favorited ? "fill-brand text-brand" : "text-brand"}`} />
          </button>
        )}
        <span className="absolute bottom-2.5 left-2.5 rounded-full bg-brand/85 px-2.5 py-1 text-[11px] font-medium text-white">
          {listing.condition}
        </span>
        {reduced && !listing.sold && (
          <span className="absolute left-2.5 top-2.5 rounded-full bg-price px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
            Reduced
          </span>
        )}
        {listing.sold && (
          <div className="absolute inset-0 flex items-center justify-center bg-brand/45">
            <span className="rotate-[-12deg] rounded-lg border-[3px] border-white px-3.5 py-1 text-xl font-extrabold tracking-widest text-white">
              SOLD
            </span>
          </div>
        )}
      </div>
      <div className="space-y-1 p-3">
        <div className="flex items-baseline gap-2">
          <span data-testid={`listing-price-${listing.id}`} className="text-lg font-bold text-price">
            {formatMoney(listing.price, listing.currency)}
          </span>
          {reduced && (
            <span className="text-xs text-muted-foreground line-through">
              {formatMoney(listing.original_price, listing.currency)}
            </span>
          )}
        </div>
        <p className="truncate text-sm font-medium text-foreground">{listing.title}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          <span className="truncate">{listing.location}</span>
        </div>
        {listing.seller?.verified && (
          <div className="flex items-center gap-1 text-xs font-semibold text-sky-600">
            <ShieldCheck className="h-3 w-3" /> Verified seller
          </div>
        )}
      </div>
    </div>
  );
}
